import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storageApi } from '../../lib/api';
import { DataTable } from '../../components/ui/DataTable';
import { AlertSeverityBadge } from '../../components/ui/StatusBadge';
import { formatDate, capitalize } from '../../lib/utils';
import { useAuth } from '../../hooks/useAuth';
const columns = (onAcknowledge, canEdit) => [
    {
        key: 'severity',
        header: 'Severity',
        render: (row) => _jsx(AlertSeverityBadge, { status: row.severity }),
        className: 'w-24',
    },
    {
        key: 'type',
        header: 'Alert',
        render: (row) => (_jsxs("div", { children: [_jsx("p", { className: "font-medium text-gray-900", children: capitalize(row.alertType.replace(/_/g, ' ')) }), _jsx("p", { className: "text-xs text-gray-500", children: row.alertType.replace(/_/g, ' ') })] })),
    },
    {
        key: 'value',
        header: 'Value',
        render: (row) => row.triggerValue !== null
            ? _jsxs("span", { className: "tabular-nums", children: [row.triggerValue, " (threshold: ", row.thresholdValue, ")"] })
            : '—',
        className: 'text-sm text-gray-600',
    },
    {
        key: 'raised',
        header: 'Raised',
        render: (row) => formatDate(row.triggeredAt),
        className: 'w-28 text-gray-500 text-xs',
    },
    {
        key: 'resolved',
        header: 'Resolved',
        render: (row) => row.resolvedAt
            ? formatDate(row.resolvedAt)
            : (_jsx("span", { className: "badge bg-red-100 text-red-700", children: "Open" })),
        className: 'w-28',
    },
    {
        key: 'actions',
        header: '',
        render: (row) => !row.resolvedAt && canEdit
            ? (_jsx("button", { onClick: (e) => { e.stopPropagation(); onAcknowledge(row); }, className: "text-xs text-wine-700 hover:text-wine-900 font-medium", children: "Acknowledge" }))
            : null,
        className: 'w-28',
    },
];
export function AlertsPage() {
    const { activeAccount, canEdit } = useAuth();
    const queryClient = useQueryClient();
    const { data, isLoading } = useQuery({
        queryKey: ['alerts', activeAccount?.id],
        queryFn: () => storageApi.getAlerts(),
        enabled: Boolean(activeAccount),
    });
    const acknowledgeMutation = useMutation({
        mutationFn: ({ locationId, alertId }) => storageApi.acknowledgeAlert(locationId, alertId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alerts'] }),
    });
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("h1", { className: "page-title", children: "Environment Alerts" }), _jsxs("p", { className: "text-sm text-gray-500 mt-0.5", children: [data?.alerts?.filter((a) => !a.resolvedAt).length ?? 0, " open alerts"] })] }), _jsx("div", { className: "card overflow-hidden", children: _jsx(DataTable, { columns: columns((alert) => acknowledgeMutation.mutate({ locationId: alert.storageLocationId, alertId: alert.id }), canEdit), data: data?.alerts ?? [], isLoading: isLoading, rowKey: (row) => row.id, emptyTitle: "No alerts", emptyDescription: "Environment alerts will appear here when thresholds are exceeded" }) })] }));
}
